import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Carta con inclinacion 3D, como en Pokémon TCG Live: se mantiene pulsado y al
 * mover el puntero la carta gira siguiendo la mano.
 *
 * Detalles que hacen que se sienta bien y no a "div que rota":
 *
 *  - Mientras se arrastra NO hay transicion. Cualquier suavizado aqui se percibe
 *    como retraso: el giro tiene que ir pegado al puntero. El suavizado entra
 *    solo al soltar, para que vuelva sola con un rebote corto.
 *  - El brillo se mueve en direccion contraria a la inclinacion, que es como se
 *    comporta un reflejo real sobre una superficie satinada.
 *  - setPointerCapture permite salirse de la carta sin perder el arrastre.
 *  - Con prefers-reduced-motion el efecto se desactiva por completo.
 */

const MAX_TILT = 16;   // grados
const GRAB_SCALE = 1.04;
/** Pixeles de tolerancia por debajo de los cuales soltar cuenta como clic. */
const UMBRAL_CLIC = 6;

export default function CardTilt({ src, alt, className = '', holo = false, onActivate, etiquetaBoton, sinArte, preSrc }) {
  const ref = useRef(null);
  /*
   * Donde empezo la pulsacion, para distinguir un CLIC de un ARRASTRE.
   *
   * Las dos cosas empiezan igual -- `pointerdown` sobre la carta -- asi que sin
   * esto no hay forma de saber si el usuario queria inclinarla o ampliarla. Si
   * al soltar el puntero apenas se ha movido, era un clic. El margen existe
   * porque nadie pulsa sin desplazar un pixel, y en tactil el dedo se mueve
   * bastante mas que un raton.
   */
  const origen = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0, gx: 50, gy: 50 });
  const [loaded, setLoaded] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const apply = useCallback((clientX, clientY) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // -1 .. 1 desde el centro
    const nx = ((clientX - r.left) / r.width) * 2 - 1;
    const ny = ((clientY - r.top) / r.height) * 2 - 1;
    const clamp = (v) => Math.max(-1, Math.min(1, v));
    setTilt({
      x: clamp(-ny) * MAX_TILT,
      y: clamp(nx) * MAX_TILT,
      // signo invertido: el reflejo barre al lado contrario del que inclinas
      gx: (clamp(-nx) * 0.5 + 0.5) * 100,
      gy: (clamp(-ny) * 0.5 + 0.5) * 100,
    });
  }, []);

  // setPointerCapture lanza NotFoundError si el puntero ya no esta activo
  // (raton que sale de la ventana, evento sintetico). No es motivo para
  // abortar el arrastre: sin captura sigue funcionando, solo se pierde el
  // seguimiento fuera de la carta.
  const capture = (el, id, on) => {
    try {
      if (on) el.setPointerCapture?.(id);
      else el.releasePointerCapture?.(id);
    } catch {
      /* sin captura */
    }
  };

  const onPointerDown = (e) => {
    origen.current = { x: e.clientX, y: e.clientY };
    // Con movimiento reducido no hay inclinacion, pero SI debe poder ampliarse:
    // el zoom es una funcion, no un adorno.
    if (reduced) return;
    capture(e.currentTarget, e.pointerId, true);
    setDragging(true);
    apply(e.clientX, e.clientY);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    apply(e.clientX, e.clientY);
  };

  const release = (e) => {
    const o = origen.current;
    origen.current = null;
    if (o && onActivate) {
      const movido = Math.hypot(e.clientX - o.x, e.clientY - o.y);
      if (movido < UMBRAL_CLIC) onActivate();
    }
    if (!dragging) return;
    capture(e.currentTarget, e.pointerId, false);
    setDragging(false);
    setTilt({ x: 0, y: 0, gx: 50, gy: 50 });
  };

  return (
    <div className={`tilt ${className}`}>
      <div
        ref={ref}
        className={
          'tilt__inner' +
          (dragging ? ' is-dragging' : '') +
          (loaded ? ' is-loaded' : '') +
          (holo ? ' is-holo' : '')
        }
        style={{
          '--rx': `${tilt.x}deg`,
          '--ry': `${tilt.y}deg`,
          '--gx': `${tilt.gx}%`,
          '--gy': `${tilt.gy}%`,
          '--grab': dragging ? GRAB_SCALE : 1,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={release}
        onPointerCancel={release}
        /* Con `onActivate` la carta pasa a ser un control, y un control tiene
           que poder usarse sin raton. Sin esto, ampliar era la unica funcion del
           modal inalcanzable con el teclado. */
        role={onActivate ? 'button' : undefined}
        tabIndex={onActivate ? 0 : undefined}
        aria-label={onActivate ? etiquetaBoton : undefined}
        onKeyDown={
          onActivate
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onActivate();
                }
              }
            : undefined
        }
      >
        {src ? (
          <>
            {/*
              La version en BAJA mientras baja la alta. Es la misma imagen que
              la rejilla, asi que casi siempre esta en la cache del navegador:
              la carta aparece al instante y la alta la reemplaza al llegar,
              en vez de un hueco brillante hasta que la CDN responda.
            */}
            {preSrc && preSrc !== src && !loaded && (
              <img className="tilt__pre" src={preSrc} alt="" aria-hidden="true" draggable={false} />
            )}
            <img
              className="tilt__img"
              src={src}
              alt={alt}
              draggable={false}
              onLoad={() => setLoaded(true)}
            />
          </>
        ) : sinArte ? (
          /* Sin `src` Y con texto de aviso: recuadro QUIETO con el mensaje. El
             placeholder brillante de abajo significa "cargando", y usarlo para
             una carta que no tiene arte se leia como una carga infinita o un
             fallo. Lo que falta es la ilustracion, y se dice con palabras. */
          <div className="tilt__placeholder tilt__placeholder--aviso">
            <span className="tilt__sinarte">{sinArte}</span>
          </div>
        ) : (
          <div className="tilt__placeholder" />
        )}
        {/* Solo en cartas que de verdad son holograficas: ponerlo en todas
            haria que una comun se vea como una rara, que es justo la
            informacion que uno mira en esta pantalla. */}
        {holo && <span className="tilt__holo" aria-hidden="true" />}
        {/* El brillo va contrario a la inclinacion: asi se mueve un reflejo real. */}
        <span className="tilt__glare" aria-hidden="true" />
        <span className="tilt__edge" aria-hidden="true" />
      </div>
    </div>
  );
}
